/**
 * Count Stock (migration 0088; the inventory plan's §15.2 and §15.10, step
 * I2b). The sheet a count is taken from: for a date and a stock account,
 * every pool with what the books hold. The operator writes what is on the
 * shelf beside the ones counted, and Post Count records the count and posts
 * the differences in one entry:
 *
 * - SHORT: goods out to the count's expense (6870 Inventory Shrinkage unless
 *   another is chosen), first in, first out, priced when it posts;
 * - OVER: goods in at the cost of one unit of the pool's latest layer, which
 *   the sheet shows and the operator may change;
 * - a pool left blank is not counted, so a count of a few shelves is a count.
 *
 * The counts already recorded are listed below, newest first. A count is the
 * record of what was counted and is never changed; a wrong one is corrected
 * by reversing its entry and counting again.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessNotice } from '../components/AccessNotice.js';
import { InfoNote } from '../components/InfoNote.js';
import { Toast } from '../components/Toast.js';
import { localToday } from '../components/GoodsReturnForm.js';
import { useCan } from '../auth/AuthProvider.js';
import { ApiError, api, type Account, type CountSheetPool, type StockCount } from '../api.js';
import { formatAmountInput, parseCents } from '../lib/money.js';
import { showQuantity } from './ItemsPage.js';

const QUANTITY = /^\d{1,14}(\.\d{1,4})?$/;

/** Ten-thousandths, so a difference is exact: "2.5" is 25000. */
function units(quantity: string): number {
  const [whole, fraction = ''] = quantity.split('.');
  return Number(whole) * 10_000 + Number(fraction.padEnd(4, '0').slice(0, 4));
}

function showUnits(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  return showQuantity(`${sign}${Math.floor(abs / 10_000)}.${String(abs % 10_000).padStart(4, '0')}`);
}

const poolKey = (pool: Pick<CountSheetPool, 'itemId' | 'fundId' | 'projectId'>) => [pool.itemId, pool.fundId ?? '', pool.projectId ?? ''].join('|');

export function CountStockPage() {
  const mayEdit = useCan()('edit_entries');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountId, setAccountId] = useState('');
  const [adjustmentId, setAdjustmentId] = useState('');
  const [date, setDate] = useState(localToday);
  const [note, setNote] = useState('');
  const [sheet, setSheet] = useState<CountSheetPool[]>([]);
  const [counts, setCounts] = useState<StockCount[]>([]);
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [unitCosts, setUnitCosts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let current = true;
    Promise.all([api.listAccounts(), api.listCounts()])
      .then(([accountResult, countResult]) => {
        if (!current) return;
        setAccounts(accountResult.accounts);
        setCounts(countResult.counts);
        const stock = accountResult.accounts.find((account) => account.inventoryRole === 'stock' && account.isActive);
        setAccountId((now) => now || stock?.id || '');
        const shrinkage = accountResult.accounts.find((account) => account.code === '6870' && account.type === 'expense');
        setAdjustmentId((now) => now || shrinkage?.id || '');
      })
      .catch((caught: unknown) => current && setError(caught instanceof ApiError ? caught.message : 'Could not read the stock accounts.'));
    return () => {
      current = false;
    };
  }, []);

  const loadSheet = useCallback(() => {
    if (accountId === '' || date === '') return;
    let current = true;
    api
      .countSheet(accountId, date)
      .then((result) => {
        if (!current) return;
        setSheet(result.pools);
        setUnitCosts(
          Object.fromEntries(result.pools.map((pool) => [poolKey(pool), pool.unitCostCents === null ? '' : formatAmountInput(pool.unitCostCents)])),
        );
      })
      .catch((caught: unknown) => current && setError(caught instanceof ApiError ? caught.message : 'Could not read the stock on that date.'));
    return () => {
      current = false;
    };
  }, [accountId, date]);
  useEffect(loadSheet, [loadSheet]);

  const stockAccounts = useMemo(() => accounts.filter((account) => account.inventoryRole === 'stock'), [accounts]);
  const expenses = useMemo(() => accounts.filter((account) => account.type === 'expense' && account.isActive), [accounts]);
  const byFund = sheet.some((pool) => pool.fundName !== null);
  const byProject = sheet.some((pool) => pool.projectName !== null);
  const accountName = (id: string) => {
    const account = accounts.find((candidate) => candidate.id === id);
    return account ? `${account.code} ${account.name}` : '';
  };

  async function post() {
    const lines = sheet
      .map((pool) => ({ pool, text: (counted[poolKey(pool)] ?? '').trim() }))
      .filter(({ text }) => text !== '');
    if (lines.length === 0) {
      setError('Write what is on the shelf beside at least one item.');
      return;
    }
    const bad = lines.find(({ text }) => !QUANTITY.test(text));
    if (bad) {
      setError(`${bad.pool.item}: a count is a number of the item’s unit, to at most four decimal places.`);
      return;
    }
    if (adjustmentId === '') {
      setError('Choose the expense a difference lands on.');
      return;
    }
    const body = lines.map(({ pool, text }) => {
      const over = units(text) > units(pool.onHand);
      const cost = (unitCosts[poolKey(pool)] ?? '').trim();
      const cents = cost === '' ? null : parseCents(cost);
      return {
        itemId: pool.itemId,
        accountId,
        fundId: pool.fundId,
        projectId: pool.projectId,
        counted: text,
        // The operator's figure only where it differs from the latest layer's, which the server reads itself.
        ...(over && cents !== null && cents !== pool.unitCostCents && { unitCostCents: cents }),
      };
    });
    setBusy(true);
    setError(null);
    try {
      const result = await api.postCount({ countedOn: date, adjustmentAccountId: adjustmentId, note: note.trim() === '' ? null : note.trim(), lines: body });
      setNotice(result.count.headerId === null ? 'Counted: every item agreed with the books.' : 'Counted, and the differences posted.');
      setCounted({});
      setNote('');
      loadSheet();
      setCounts((await api.listCounts()).counts);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not record the count.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="count-stock">
      <h1>Count Stock</h1>
      <p className="muted">
        <Link to="/items">Items</Link> · What the books hold on a day, beside what is on the shelf.{' '}
        <InfoNote label="What A Count Does">
          Write what you counted beside each item you counted, and leave the rest blank. Post Count records the count
          and posts the differences in one entry: goods short go out to the expense you choose, first in, first out;
          goods found come in at the cost of one unit shown, which you may change. A count is never edited. If one is
          wrong, reverse its entry and count again.
        </InfoNote>
      </p>
      <AccessNotice capability="edit_entries" what="the counts" />
      {notice && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      <div className="row">
        <label>
          Date{' '}
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        <label>
          Stock Account{' '}
          <select aria-label="Stock Account" value={accountId} onChange={(event) => setAccountId(event.target.value)}>
            {stockAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.code} {account.name}
              </option>
            ))}
          </select>
        </label>
        {mayEdit && (
          <label>
            Differences Go To{' '}
            <select aria-label="Differences Go To" value={adjustmentId} onChange={(event) => setAdjustmentId(event.target.value)}>
              <option value="">Choose An Expense</option>
              {expenses.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.code} {account.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {stockAccounts.length === 0 && (
        <p className="notice notice-warn">No account holds stock yet. Mark one with Hold Stock on Accounts.</p>
      )}
      <div className="table-scroll">
        <table className="lines" aria-label="Count Sheet">
          <thead>
            <tr>
              <th>Item</th>
              {byFund && <th>Fund</th>}
              {byProject && <th>Program Or Project</th>}
              <th className="num">Books</th>
              {mayEdit && <th className="num">Counted</th>}
              {mayEdit && <th className="num">Difference</th>}
              {mayEdit && <th className="num">Cost Of One</th>}
            </tr>
          </thead>
          <tbody>
            {sheet.map((pool) => {
              const key = poolKey(pool);
              const text = (counted[key] ?? '').trim();
              const difference = text !== '' && QUANTITY.test(text) ? units(text) - units(pool.onHand) : null;
              return (
                <tr key={key}>
                  <td>{pool.item}</td>
                  {byFund && <td>{pool.fundName ?? ''}</td>}
                  {byProject && <td>{pool.projectName ?? ''}</td>}
                  <td className="num">
                    {showQuantity(pool.onHand)} {pool.unit}
                  </td>
                  {mayEdit && (
                    <td className="num">
                      <input
                        aria-label={`Counted: ${pool.item}${pool.fundName ? `, ${pool.fundName}` : ''}${pool.projectName ? `, ${pool.projectName}` : ''}`}
                        className="line-quantity"
                        inputMode="decimal"
                        value={counted[key] ?? ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          setCounted((current) => ({ ...current, [key]: value }));
                        }}
                      />
                    </td>
                  )}
                  {mayEdit && (
                    <td className="num">
                      {difference === null ? '' : difference === 0 ? 'Agrees' : `${difference > 0 ? '+' : ''}${showUnits(difference)} ${pool.unit}`}
                    </td>
                  )}
                  {mayEdit && (
                    <td className="num">
                      {difference !== null && difference > 0 ? (
                        <input
                          aria-label={`Cost Of One: ${pool.item}`}
                          className="line-quantity"
                          inputMode="decimal"
                          value={unitCosts[key] ?? ''}
                          placeholder="0.00"
                          onChange={(event) => {
                            const value = event.target.value;
                            setUnitCosts((current) => ({ ...current, [key]: value }));
                          }}
                        />
                      ) : (
                        ''
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {sheet.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  Nothing has come in on this account by {date}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {mayEdit && (
        <div className="row">
          <label>
            Note{' '}
            <input aria-label="Note" value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <button type="button" disabled={busy} onClick={() => void post()}>
            Post Count
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <h2>Counts Recorded</h2>
      {counts.length === 0 ? (
        <p className="muted">No count recorded yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines" aria-label="Counts Recorded">
            <thead>
              <tr>
                <th>Date</th>
                <th>Items Counted</th>
                <th>Differences</th>
                <th>Expense</th>
                <th>Entry</th>
              </tr>
            </thead>
            <tbody>
              {counts.map((count) => (
                <tr key={count.id}>
                  <td>{count.countedOn}</td>
                  <td>{count.lines.map((line) => line.item).join(', ')}</td>
                  <td>{count.lines.filter((line) => line.detailId !== null).length}</td>
                  <td>{accountName(count.adjustmentAccountId)}</td>
                  <td>
                    {count.headerId === null ? (
                      <span className="muted">None Needed</span>
                    ) : (
                      <>
                        <Link to={`/journals/${count.headerId}`}>Open Entry</Link>
                        {count.reversed && <span className="tag">Reversed</span>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
